/**
 * Общая партия на двоих: одно состояние доски на сервере, оба клиента его
 * читают и пишут.
 *
 * Почему опрос, а не вебсокет: у нас WS занят рыночными данными и пушит по
 * подпискам на символы; заводить туда игровые комнаты ради двух ходов в минуту
 * — лишняя сложность. Раз в 1.5 секунды более чем достаточно для шахмат, а на
 * своём ходу опрос вообще не нужен: ждём мы только соперника.
 *
 * Правила проверяет доска на клиенте, очередь хода — сервер. Он же и отказывает
 * (409), если попытаться сходить не в свою очередь; из-за отсутствия такой
 * проверки в прошлой версии доски у игроков расходились.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { apiClient } from '../api/client';
import i18n from '../i18n';

export interface SharedGame<B = unknown> {
  id: string;
  kind: string;
  players: string[];
  turn: string;
  board: B | null;
  status: 'pending' | 'active' | 'finished';
  winner: string | null;
  created_by: string;
  /** Режим (сапёр: hunt | classic). */
  mode?: string | null;
  /** Партия-реванш, если кто-то уже нажал «Ещё раз». */
  rematch?: string | null;
}

interface Options<B> {
  /** Меня зовут так — от этого зависит, мой ли ход. */
  me: string;
  /** Применить состояние соперника к своей доске. */
  onRemoteBoard: (board: B) => void;
}

export function useSharedGame<B>({ me, onRemoteBoard }: Options<B>) {
  const [params] = useSearchParams();
  const gameId = params.get('game');
  const [game, setGame] = useState<SharedGame<B> | null>(null);
  const lastApplied = useRef<string>('');

  const pull = useCallback(async () => {
    if (!gameId) return;
    try {
      const r = await apiClient.raw.get(`/api/social/games/${gameId}`);
      const g: SharedGame<B> = r.data;
      setGame(g);
      // Применяем только ЧУЖОЙ ход и только если состояние изменилось: иначе
      // опрос затирал бы фигуру, которую игрок как раз двигает.
      const sig = JSON.stringify(g.board);
      if (g.board && sig !== lastApplied.current) {
        lastApplied.current = sig;
        onRemoteBoard(g.board);
      }
    } catch (e: any) {
      if (e?.response?.status === 404) toast.error(i18n.t('useSharedGame:notFound'));
    }
  }, [gameId, onRemoteBoard]);

  useEffect(() => {
    if (!gameId) return;
    void pull();
    const id = setInterval(pull, 1500);
    return () => clearInterval(id);
  }, [gameId, pull]);

  /** Отправить свой ход. Возвращает false, если сервер не принял. */
  const pushMove = useCallback(async (board: B, opts: {
    note?: string; finished?: boolean; winner?: string | null;
    /** Чей ход после этого. По умолчанию — соперника; в сапёре-охоте
     *  нашедший мину ходит ещё раз. */
    turn?: string;
  } = {}) => {
    if (!gameId || !game) return true;          // одиночная игра — просто ходим
    const other = game.players.find(p => p !== me) || me;
    try {
      const r = await apiClient.raw.post(`/api/social/games/${gameId}/move`, {
        board, turn: opts.turn ?? other, note: opts.note ?? null,
        finished: !!opts.finished, winner: opts.winner ?? null,
      });
      lastApplied.current = JSON.stringify(r.data?.board);
      setGame(r.data);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.status === 409 ? i18n.t('useSharedGame:opponentTurn')
                                              : (e?.response?.data?.detail || i18n.t('useSharedGame:moveRejected')));
      void pull();
      return false;
    }
  }, [gameId, game, me, pull]);

  const accept = useCallback(async () => {
    if (!gameId) return;
    try {
      const r = await apiClient.raw.post(`/api/social/games/${gameId}/accept`);
      setGame(r.data);
      toast.success(i18n.t('useSharedGame:started'));
    } catch { toast.error(i18n.t('useSharedGame:acceptFailed')); }
  }, [gameId]);

  const resign = useCallback(async () => {
    if (!gameId) return;
    try {
      const r = await apiClient.raw.post(`/api/social/games/${gameId}/resign`);
      setGame(r.data);
    } catch { /* уже закончена */ }
  }, [gameId]);

  const opponent = game ? (game.players.find(p => p !== me) || '') : '';
  return {
    gameId, game, opponent,
    isMultiplayer: !!gameId,
    myTurn: !!game && game.turn === me && game.status === 'active',
    // Первый игрок (создатель) ходит белыми — так же, как в приглашении.
    myColorIsFirst: !!game && game.players[0] === me,
    pushMove, accept, resign, refresh: pull,
  };
}
